import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { timezoneStore } from "@/api/hooks/useConfig";
import { getPeriodLabelValueFromStartTime } from "@/components/v2/TrendView/utils/formatting";
import { getPeriodFilterValue } from "@/components/v2/TrendView/utils/timeframe";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { ContaminationPageContext } from "@/pages/contamination/context";
import { getDataWithCurrentMonth } from "@/pages/contamination/utils";
import { useTrans } from "@/translations";

import { useCallback, useContext, useMemo } from "react";

import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FilterTimeFrameValue } from "server/config-api/index.js";
import { Granularity } from "server/dwhApi/common.js";

import type { MonthlyRateRow } from "../types";
import { getPotentialRecyclingRate } from "../utils";
import { LostRecyclingRateTooltipContent } from "./graphTooltip";

const DEFAULT_RECYCLING_TARGET = 80;

export const LostRecyclingRateGraph: React.FC = () => {
  const { t, language } = useTrans();
  const { timezone } = timezoneStore();
  const { currentTerminal } = useTerminalsState();
  const { selectedTenantCategoryIds } = useContext(ContaminationPageContext);

  const periodFilterValue = useMemo(
    () =>
      getPeriodFilterValue({
        period: FilterTimeFrameValue.Last12Months,
        timezone,
      }),
    [timezone],
  );

  const { data: periodData, isLoading: isPeriodLoading } =
    trpc.dwh.terminals.getPeriods.useQuery(
      {
        ...periodFilterValue,
        timeZone: timezone,
        terminalId: currentTerminal?.id || "",
        granularity: Granularity.MONTH,
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalPeriods",
            periodFilterValue.startTimeISO,
            periodFilterValue.endTimeISO,
            timezone,
            currentTerminal?.id,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!currentTerminal,
      },
    );

  const dataWithCurrentMonth = useMemo(
    () =>
      getDataWithCurrentMonth({
        periodData,
        period: FilterTimeFrameValue.Last12Months,
      }),
    [periodData],
  );

  const getPeriodLabelValue = useCallback(
    (startTime: string) =>
      getPeriodLabelValueFromStartTime({
        period: FilterTimeFrameValue.Last12Months,
        granularity: Granularity.MONTH,
        startTime,
        language,
        timezone,
      }),
    [language, timezone],
  );

  const graphData: MonthlyRateRow[] = useMemo(() => {
    if (!periodData?.periods) return [];

    return dataWithCurrentMonth
      .map((d) => ({
        month: getPeriodLabelValue(d.startTime),
        actual: +(d.terminal.sortingDegree * 100).toFixed(1),
        potential: getPotentialRecyclingRate(d.terminal),
      }))
      .map((d) => ({ ...d, gap: +(d.potential - d.actual).toFixed(1) }));
  }, [periodData, dataWithCurrentMonth, getPeriodLabelValue]);

  const { terminalConfig } = useConfigService();

  const target = useMemo(
    () => terminalConfig?.recyclingTarget || DEFAULT_RECYCLING_TARGET,
    [terminalConfig],
  );

  return (
    <div className="min-w-[240px] flex-1">
      <h2 className="font-dm-sans text-[18px] font-bold">
        {t("contaminationPage:recyclingRateLostTitle")}
      </h2>
      <p className="text-grey6 font-dm-sans mt-[4px] text-[13px]">
        {t("contaminationPage:recyclingRateLostSubtitle")}
      </p>

      <ViewWithLoader isLoading={isPeriodLoading}>
        <div className="mt-[18px] h-[214px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={graphData}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid vertical={false} stroke="#EEE7E7" />
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#6F6C6C", fontSize: 10.5 }}
                dy={8}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#B2ABAB", fontSize: 10 }}
                tickFormatter={(value: number) => `${value}%`}
                width={36}
                domain={[0, 100]}
              />
              <Tooltip content={<LostRecyclingRateTooltipContent />} />
              <ReferenceLine
                y={target}
                stroke="#467432"
                strokeWidth={1.5}
                strokeDasharray="6 4"
                label={{
                  value: t("contaminationPage:recyclingRateLostTargetLabel", {
                    value: target,
                  }),
                  position: "insideTopRight",
                  fill: "#467432",
                  fontSize: 10,
                  fontWeight: 600,
                }}
              />
              <Area
                dataKey="actual"
                stackId="gap"
                stroke="none"
                fill="transparent"
                isAnimationActive={false}
                legendType="none"
              />
              <Area
                dataKey="gap"
                stackId="gap"
                stroke="none"
                fill="#1D1C1C"
                fillOpacity={0.12}
                isAnimationActive={false}
                legendType="none"
              />
              <Line
                type="monotone"
                dataKey="potential"
                stroke="#467432"
                strokeWidth={3}
                dot={{
                  r: 3.5,
                  fill: "#fff",
                  stroke: "#467432",
                  strokeWidth: 2,
                }}
                activeDot={{
                  r: 4.5,
                  fill: "#fff",
                  stroke: "#467432",
                  strokeWidth: 2,
                }}
              />
              <Line
                type="monotone"
                dataKey="actual"
                stroke="#1D1C1C"
                strokeWidth={3}
                dot={{
                  r: 3.5,
                  fill: "#fff",
                  stroke: "#1D1C1C",
                  strokeWidth: 2,
                }}
                activeDot={{
                  r: 4.5,
                  fill: "#fff",
                  stroke: "#1D1C1C",
                  strokeWidth: 2,
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-[14px] flex flex-wrap gap-[18px] text-[12px]">
          <span className="inline-flex items-center gap-[7px]">
            <span className="inline-block h-0 w-[16px] border-t-[3px] border-[#467432]" />
            {t("contaminationPage:recyclingRateLostWithout")}
          </span>
          <span className="inline-flex items-center gap-[7px]">
            <span className="inline-block h-0 w-[16px] border-t-[3px] border-[#1D1C1C]" />
            {t("contaminationPage:recyclingRateLostActual")}
          </span>
          <span className="inline-flex items-center gap-[7px]">
            <span className="inline-block h-[11px] w-[14px] rounded-[2px] bg-[#1D1C1C]/10" />
            {t("contaminationPage:recyclingRateLostToContamination")}
          </span>
        </div>
      </ViewWithLoader>
    </div>
  );
};
